import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useExport } from '@/hooks/ui/useExport';
import { useTranslation } from '@/i18n';
import { attendanceDashboardService, attendanceExportService } from '@/api/domains/attendance';
import type {
  AttendanceDashboard,
  DashboardMark,
  DashboardMonthly,
  DashboardWeekly,
} from '@/api/domains/attendance';
import type { RootState } from '@/store';
import { getAPIFormat } from '@/types/fileExport.types';
import type { UIExportFormat } from '@/types/fileExport.types';
import { useMonthSummaryFilters } from './useMonthSummaryFilters';
import type { UseMonthSummaryFiltersResult } from './useMonthSummaryFilters';


// El endpoint de daily-status dispara un SP costoso en base de datos.
// El botón manual queda bloqueado REFRESH_COOLDOWN_MS después de cada uso —
// solo para evitar doble clic accidental, no para limitar el refresco "on demand".
const REFRESH_COOLDOWN_MS = 3 * 1000;

function buildLastUpdateText(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

type TranslateFn = (key: string, options?: Record<string, string | number>) => string;

function buildHeaderDateLabel(date: Date, translate: TranslateFn): string {
  const dayIndex = (date.getDay() + 6) % 7;
  const day = String(date.getDate()).padStart(2, '0');
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  const weekday = translate(`attendance:dashboard.weekdays.${dayIndex}`).toUpperCase();
  const month = translate(`attendance:dashboard.months.${monthIndex}`).toUpperCase();
  return translate('attendance:dashboard.headerDateFormat', { weekday, day, month, year: String(year) });
}

function buildSituationDateLabel(date: Date, translate: TranslateFn): string {
  const day = String(date.getDate()).padStart(2, '0');
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  const month = translate(`attendance:dashboard.months.${monthIndex}`).toUpperCase();
  return translate('attendance:dashboard.situationDateFormat', { day, month, year: String(year) });
}

export type DashboardTab = 'mi-asistencia' | 'mi-equipo' | 'empresa';

export interface UseAttendanceDashboardResult extends UseMonthSummaryFiltersResult {
  employeeFirstName: string;
  activeTab: DashboardTab;
  setActiveTab: (tab: DashboardTab) => void;
  dashboard: AttendanceDashboard | null;
  weeklyData: DashboardWeekly | null;
  monthly: DashboardMonthly | null;
  todayMarks: DashboardMark[];
  isLoadingMain: boolean;
  isLoadingMonthly: boolean;
  isLoadingMarks: boolean;
  isLoadingWeekly: boolean;
  lastUpdate: string;
  headerDateLabel: string;
  situationDateLabel: string;
  refresh: () => void;
  canManualRefresh: boolean;
  isActiveTabReady: boolean;
  registerMyTeamLoading: (isLoading: boolean) => void;
  registerMyTeamRefetch: (refetch: () => void) => void;
  onExportDashboard: (format: UIExportFormat) => Promise<void>;
  isExportingDashboard: boolean;
}

export const useAttendanceDashboard = (): UseAttendanceDashboardResult => {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal: createDashboardSignal } = useAbortController();
  const { createSignal: createMarksSignal } = useAbortController();
  const { createSignal: createWeeklySignal } = useAbortController();
  const user = useSelector((state: RootState) => state.auth.user);
  const employeeId = user?.idEmpleado;

  const monthFilters = useMonthSummaryFilters();
  const { selectedYear, selectedMonth, selectedCutId } = monthFilters;

  const today = useMemo(() => new Date(), []);
  const [activeTab, setActiveTabState] = useState<DashboardTab>('mi-asistencia');

  const [dashboard, setDashboard] = useState<AttendanceDashboard | null>(null);
  const [weeklyData, setWeeklyData] = useState<DashboardWeekly | null>(null);
  const [monthly, setMonthly] = useState<DashboardMonthly | null>(null);
  const [todayMarks, setTodayMarks] = useState<DashboardMark[]>([]);
  const [isLoadingMain, setIsLoadingMain] = useState(true);
  const [isLoadingMonthly, setIsLoadingMonthly] = useState(false);
  const [isLoadingMarks, setIsLoadingMarks] = useState(true);
  const [isLoadingWeekly, setIsLoadingWeekly] = useState(false);
  const [isMyTeamLoading, setIsMyTeamLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<string>(buildLastUpdateText(today));
  const [canManualRefresh, setCanManualRefresh] = useState(true);

  const headerDateLabel = useMemo(() => buildHeaderDateLabel(today, translate), [today, translate]);
  const situationDateLabel = useMemo(
    () => buildSituationDateLabel(today, translate),
    [today, translate],
  );

  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const myTeamRefetchRef = useRef<(() => void) | null>(null);

  // Cambiar a "mi-equipo" marca su data como "cargando" de inmediato (antes de que el
  // propio tab monte y lo reporte), para que el botón de refresco nunca muestre
  // habilitado con datos de una visita anterior a esa pestaña.
  const setActiveTab = useCallback((tab: DashboardTab) => {
    if (tab === 'mi-equipo') setIsMyTeamLoading(true);
    setActiveTabState(tab);
  }, []);

  const fetchDashboard = useCallback(async () => {
    if (!employeeId) return;
    const signal = createDashboardSignal();
    setIsLoadingMain(true);
    try {
      const response = await attendanceDashboardService.getDashboard(signal);
      // Una respuesta ABORTED significa que una llamada más nueva ya reemplazó a esta;
      // esa llamada más nueva es responsable de apagar el loading, no esta.
      if (response.error?.code === 'ABORTED') return;
      setIsLoadingMain(false);
      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.loadError'));
        return;
      }
      setDashboard(response.data);
      setLastUpdate(buildLastUpdateText(new Date()));
    } catch (error) {
      setIsLoadingMain(false);
      throw error;
    }
  }, [employeeId, showApiError, translate, createDashboardSignal]);

  const fetchCurrentWeek = useCallback(async () => {
    if (!employeeId) return;
    const signal = createWeeklySignal();
    setIsLoadingWeekly(true);
    try {
      const response = await attendanceDashboardService.getCurrentWeek(signal);
      // Una respuesta ABORTED significa que una llamada más nueva ya reemplazó a esta;
      // esa llamada más nueva es responsable de apagar el loading, no esta.
      if (response.error?.code === 'ABORTED') return;
      setIsLoadingWeekly(false);
      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.weeklyLoadError'));
        return;
      }
      setWeeklyData(response.data);
    } catch (error) {
      setIsLoadingWeekly(false);
      throw error;
    }
  }, [employeeId, showApiError, translate, createWeeklySignal]);

  const fetchTodayMarks = useCallback(async () => {
    if (!employeeId) return;
    const signal = createMarksSignal();
    setIsLoadingMarks(true);
    try {
      const response = await attendanceDashboardService.getTodayMarks(signal);
      // Una respuesta ABORTED significa que una llamada más nueva ya reemplazó a esta;
      // esa llamada más nueva es responsable de apagar el loading, no esta.
      if (response.error?.code === 'ABORTED') return;
      setIsLoadingMarks(false);
      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.marksLoadError'));
        return;
      }
      setTodayMarks(response.data);
    } catch (error) {
      setIsLoadingMarks(false);
      throw error;
    }
  }, [employeeId, showApiError, translate, createMarksSignal]);

  const fetchMonthly = useCallback(async () => {
    if (!selectedYear || !selectedMonth) return;
    setIsLoadingMonthly(true);
    try {
      const cutId = selectedCutId ? parseInt(selectedCutId, 10) : undefined;
      const response = await attendanceDashboardService.getMonthlySummary({
        year: parseInt(selectedYear, 10),
        month: parseInt(selectedMonth, 10),
        monthlyCutId: cutId,
      });
      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.monthSummary.filters.loadError'));
        return;
      }
      setMonthly(response.data);
    } finally {
      setIsLoadingMonthly(false);
    }
  }, [selectedYear, selectedMonth, selectedCutId, showApiError, translate]);

  // Ejecuta las tres llamadas de "Mi asistencia" sin restricción de cooldown.
  // Usado internamente por: carga inicial y refresco manual de esa pestaña.
  const runRefresh = useCallback(() => {
    void fetchDashboard();
    void fetchTodayMarks();
    void fetchCurrentWeek();
  }, [fetchDashboard, fetchTodayMarks, fetchCurrentWeek]);

  const registerMyTeamLoading = useCallback((isLoading: boolean) => {
    setIsMyTeamLoading(isLoading);
    // Reutiliza la misma señal de "terminó de cargar" para sellar la hora de
    // actualización, igual que fetchDashboard lo hace para "Mi asistencia".
    if (!isLoading) setLastUpdate(buildLastUpdateText(new Date()));
  }, []);

  const registerMyTeamRefetch = useCallback((refetchFn: () => void) => {
    myTeamRefetchRef.current = refetchFn;
  }, []);

  // "Empresa" es un placeholder sin datos propios aún: nunca se considera listo.
  const isActiveTabReady = useMemo(() => {
    if (activeTab === 'mi-asistencia') return !isLoadingMain && !isLoadingMarks && !isLoadingWeekly;
    if (activeTab === 'mi-equipo') return !isMyTeamLoading;
    return false;
  }, [activeTab, isLoadingMain, isLoadingMarks, isLoadingWeekly, isMyTeamLoading]);

  // Expuesto al botón manual: respeta el cooldown para no saturar la BD, no hace nada
  // mientras el tab activo aún no termina su carga, y recarga solo los datos de ese tab.
  const refresh = useCallback(() => {
    if (!canManualRefresh || !isActiveTabReady) return;
    setCanManualRefresh(false);
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = setTimeout(() => {
      setCanManualRefresh(true);
    }, REFRESH_COOLDOWN_MS);
    if (activeTab === 'mi-asistencia') runRefresh();
    else if (activeTab === 'mi-equipo') myTeamRefetchRef.current?.();
  }, [activeTab, canManualRefresh, isActiveTabReady, runRefresh]);

  // Carga inicial al montar el componente.
  useEffect(() => {
    runRefresh();
  }, [runRefresh]);

  // Re-fetch del resumen mensual cuando cambia el año, mes o corte seleccionado.
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;
    void fetchMonthly();
  }, [selectedYear, selectedMonth, selectedCutId, fetchMonthly]);

  // Limpia el timer de cooldown al desmontar.
  useEffect(() => () => {
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
  }, []);

  const {
    onExport: onExportDashboard,
    isExporting: isExportingDashboard,
  } = useExport<UIExportFormat>({
    exportFn: (format) =>
      attendanceExportService.exportDashboard({
        year: selectedYear ? parseInt(selectedYear, 10) : undefined,
        month: selectedMonth ? parseInt(selectedMonth, 10) : undefined,
        monthlyCutId: selectedCutId ? parseInt(selectedCutId, 10) : undefined,
        format: getAPIFormat(format),
      }),
    successMessage: translate('attendance:dashboard.export.successMessage'),
    errorMessage: translate('attendance:dashboard.export.errorMessage'),
  });

  const employeeFirstName = user?.nombres ?? '';

  return {
    ...monthFilters,
    employeeFirstName,
    activeTab,
    setActiveTab,
    dashboard,
    weeklyData,
    monthly,
    todayMarks,
    isLoadingMain,
    isLoadingMonthly,
    isLoadingMarks,
    isLoadingWeekly,
    lastUpdate,
    headerDateLabel,
    situationDateLabel,
    refresh,
    canManualRefresh,
    isActiveTabReady,
    registerMyTeamLoading,
    registerMyTeamRefetch,
    onExportDashboard,
    isExportingDashboard,
  };
};

export default useAttendanceDashboard;
